// Canonical runtime policy. Worker copies are generated by sync-realtime-policy.mjs.
// Provider parsing, entity replacement and liveness decisions remain regional.
export const LIVE_DATA_FRESHNESS_MS = 90_000;

const common = {
  timeZone: "Pacific/Auckland",
  liveFreshnessMs: LIVE_DATA_FRESHNESS_MS,
  serviceAlertFreshnessMs: 2_700_000,
  mapStaleFallbackMs: 60_000,
  quietHealthGraceMs: 45_000,
};

const definitions = {
  auckland: {
    ...common,
    activeStartHour: 5, activeEndHour: 1,
    activeIntervalMs: 20_000, quietIntervalMs: 300_000, quietEmptyIntervalMs: 300_000,
    quietEmptyBackoffMultiplier: 2,
    maxBackoffMs: 300_000, watchdogIntervalMs: 30_000,
    activeSuccessfulPollStaleMs: 180_000, quietSuccessfulPollStaleMs: 1_800_000,
    activeDataStaleMs: 300_000, quietDataStaleMs: 3_600_000,
    maxConsecutiveErrors: 8, vehicleRetentionMs: 120_000, recentTripFreshnessMs: 1_800_000,
  },
  wellington: {
    ...common,
    activeStartHour: 4, activeEndHour: 2,
    activeIntervalMs: 20_000, quietIntervalMs: 300_000,
    maxBackoffMs: 300_000, watchdogIntervalMs: 5_000, jobTimeoutMs: 45_000,
    alertsActiveStartHour: 5, alertsActiveEndHour: 1,
    alertsActiveIntervalMs: 900_000, alertsQuietIntervalMs: 1_800_000,
  },
  christchurch: {
    ...common,
    activeStartHour: 4, activeEndHour: 2,
    activeIntervalMs: 20_000, quietIntervalMs: 300_000,
    maxBackoffMs: 300_000, watchdogIntervalMs: 5_000, jobTimeoutMs: 45_000,
  },
};

const commonAliases = {
  REALTIME_ACTIVE_START_HOUR_NZ: "activeStartHour",
  REALTIME_ACTIVE_END_HOUR_NZ: "activeEndHour",
  REALTIME_ACTIVE_INTERVAL_MS: "activeIntervalMs",
  REALTIME_QUIET_INTERVAL_MS: "quietIntervalMs",
  REALTIME_MAX_BACKOFF_MS: "maxBackoffMs",
  WATCHDOG_INTERVAL_MS: "watchdogIntervalMs",
  JOB_TIMEOUT_MS: "jobTimeoutMs",
};
const aliases = {
  auckland: {
    ACTIVE_SERVICE_START_HOUR_NZ: "activeStartHour", ACTIVE_SERVICE_END_HOUR_NZ: "activeEndHour",
    POLL_INTERVAL_MS: "activeIntervalMs", QUIET_POLL_INTERVAL_MS: "quietIntervalMs",
    QUIET_EMPTY_POLL_INTERVAL_MS: "quietEmptyIntervalMs", ERROR_BACKOFF_MAX_MS: "maxBackoffMs",
    WATCHDOG_INTERVAL_MS: "watchdogIntervalMs", MAX_CONSECUTIVE_ERROR_POLLS: "maxConsecutiveErrors",
    ACTIVE_SUCCESSFUL_POLL_STALE_MS: "activeSuccessfulPollStaleMs",
    QUIET_SUCCESSFUL_POLL_STALE_MS: "quietSuccessfulPollStaleMs",
    ACTIVE_DATA_STALE_MS: "activeDataStaleMs", QUIET_DATA_STALE_MS: "quietDataStaleMs",
    LIVE_VEHICLE_STALE_AFTER_MS: "vehicleRetentionMs", RECENT_TRIP_STALE_AFTER_MS: "recentTripFreshnessMs",
  },
  wellington: {
    ...commonAliases,
    SERVICE_ALERTS_ACTIVE_START_HOUR_NZ: "alertsActiveStartHour",
    SERVICE_ALERTS_ACTIVE_END_HOUR_NZ: "alertsActiveEndHour",
    SERVICE_ALERTS_ACTIVE_INTERVAL_MS: "alertsActiveIntervalMs",
    SERVICE_ALERTS_QUIET_INTERVAL_MS: "alertsQuietIntervalMs",
  },
  christchurch: commonAliases,
};

export function getRealtimePolicy(region, overrides = {}) {
  if (!Object.hasOwn(definitions, region)) throw new Error(`Unknown realtime region: ${region}`);
  const policy = { ...definitions[region] };
  for (const [key, value] of Object.entries(overrides)) {
    if (!Object.hasOwn(policy, key) || key === "timeZone") throw new Error(`Unknown realtime override: ${region}.${key}`);
    const isHour = key.endsWith("Hour");
    if (!Number.isInteger(value) || value < (isHour ? 0 : 1) || (isHour && value > 23)) {
      throw new Error(`Invalid realtime override: ${region}.${key}`);
    }
    policy[key] = value;
  }
  return Object.freeze(policy);
}

// Existing environment names are explicit adapters, not a second set of defaults.
export function getWorkerRealtimePolicy(region, env = {}) {
  getRealtimePolicy(region);
  const overrides = {};
  const knownNames = new Set(Object.values(aliases).flatMap(Object.keys));
  for (const name of knownNames) {
    if (env[name] !== undefined && env[name] !== "" && !Object.hasOwn(aliases[region], name)) {
      throw new Error(`Unsupported realtime environment override: ${region}.${name}`);
    }
  }
  for (const [name, key] of Object.entries(aliases[region])) {
    if (env[name] !== undefined && env[name] !== "") overrides[key] = Number(env[name]);
  }
  const policy = getRealtimePolicy(region, overrides);
  // APIs use the canonical deadline. A worker-only override must not silently
  // make healthy quiet polls arrive later than that shared contract permits.
  const canonical = definitions[region];
  if (policy.quietIntervalMs > canonical.quietIntervalMs
    || (policy.quietEmptyIntervalMs || 0) > (canonical.quietEmptyIntervalMs || 0)) {
    throw new Error("Longer quiet polling requires updating the canonical API health policy");
  }
  return Object.freeze({ ...policy, env: Object.freeze(Object.fromEntries(
    Object.entries(aliases[region]).map(([name, key]) => [name, policy[key]]),
  )) });
}

export function isRealtimeActive(policy, now = Date.now(), alerts = false) {
  const hour = Number(new Intl.DateTimeFormat("en-NZ", {
    hour: "numeric", hourCycle: "h23", timeZone: policy.timeZone,
  }).format(new Date(now)));
  const start = alerts ? policy.alertsActiveStartHour : policy.activeStartHour;
  const end = alerts ? policy.alertsActiveEndHour : policy.activeEndHour;
  if (!Number.isInteger(start) || !Number.isInteger(end)) throw new Error("Missing active-hours policy");
  return start === end || (start < end ? hour >= start && hour < end : hour >= start || hour < end);
}

export function realtimePollInterval(policy, { now = Date.now(), empty = false, consecutiveEmptyPolls = 0, alerts = false } = {}) {
  if (alerts) return isRealtimeActive(policy, now, true) ? policy.alertsActiveIntervalMs : policy.alertsQuietIntervalMs;
  if (isRealtimeActive(policy, now)) return policy.activeIntervalMs;
  // Auckland opts into backing off only confirmed successful empty publications.
  // Nonempty quiet feeds must still arrive inside the unchanged live deadline.
  if (policy.quietEmptyBackoffMultiplier) {
    const emptyPolls = empty && Number.isInteger(consecutiveEmptyPolls) && consecutiveEmptyPolls > 0
      ? Math.min(consecutiveEmptyPolls, 32) : 0;
    return Math.min(policy.quietEmptyIntervalMs || policy.quietIntervalMs,
      policy.activeIntervalMs * policy.quietEmptyBackoffMultiplier ** emptyPolls);
  }
  return empty && policy.quietEmptyIntervalMs ? policy.quietEmptyIntervalMs : policy.quietIntervalMs;
}

// Independent queried-feed state. Provider decoding and publication stay regional.
export function createRealtimeFeedPollState(policy) {
  let nextPollAt = 0, lastSuccessfulPollAt = null, consecutiveEmptyPolls = 0, consecutiveErrors = 0;
  function schedule(now, delay) {
    // An empty quiet feed must resume active cadence at the boundary, even when
    // its five-minute deadline would otherwise extend into active service hours.
    if (!isRealtimeActive(policy, now)) for (let at = Math.floor(now / 60_000) * 60_000 + 60_000; at <= now + delay; at += 60_000) {
      if (isRealtimeActive(policy, at)) {
        const beforeBoundary = at - policy.activeIntervalMs;
        delay = (beforeBoundary > now ? beforeBoundary : at) - now;
        break;
      }
    }
    nextPollAt = now + delay;
  }
  return {
    due(now = Date.now()) { return now >= nextPollAt; },
    success({ empty, now = Date.now() }) {
      if (typeof empty !== "boolean") throw new Error("Feed success requires explicit empty evidence");
      lastSuccessfulPollAt = now;
      consecutiveErrors = 0;
      consecutiveEmptyPolls = empty ? consecutiveEmptyPolls + 1 : 0;
      schedule(now, realtimePollInterval(policy, { now, empty, consecutiveEmptyPolls }));
    },
    failure(now = Date.now()) {
      consecutiveEmptyPolls = 0;
      consecutiveErrors += 1;
      schedule(now, Math.min(policy.activeIntervalMs * consecutiveErrors, policy.maxBackoffMs));
    },
    snapshot() { return { nextPollAt, lastSuccessfulPollAt, consecutiveEmptyPolls, consecutiveErrors }; },
  };
}

// Successful empty FULL_DATASET snapshots carry no positions to age. Their
// receive time proves health even when the provider leaves its header unchanged.
export function realtimeSnapshotHealth(policy, status, { now = Date.now(), alerts = false } = {}) {
  const timestamp = (value) => value == null ? NaN
    : value instanceof Date ? value.getTime() : new Date(value).getTime();
  const receivedAt = timestamp(status?.received_at);
  const providerAt = timestamp(status?.feed_timestamp);
  const empty = status?.entity_count === 0;
  const quietEmpty = !alerts && empty && !isRealtimeActive(policy, now);
  const deadlineMs = alerts ? policy.serviceAlertFreshnessMs : quietEmpty
    ? Math.max(policy.quietIntervalMs, policy.quietEmptyIntervalMs || 0) + policy.quietHealthGraceMs
    : policy.liveFreshnessMs;
  const receivedAgeMs = now - receivedAt;
  const dataAgeMs = Number.isFinite(providerAt) ? now - providerAt : receivedAgeMs;
  const failed = status?.last_error_at != null;
  const fresh = Boolean(status) && !failed && Number.isFinite(receivedAt)
    && receivedAgeMs >= 0 && receivedAgeMs <= deadlineMs
    && (quietEmpty || (dataAgeMs >= 0 && dataAgeMs <= deadlineMs));
  return { fresh, quietEmpty: fresh && quietEmpty, deadlineMs, receivedAgeMs, dataAgeMs };
}

export function assertRealtimeSnapshot(feed) {
  if (!feed || !feed.header || typeof feed.header !== "object"
    || (feed.entity != null && !Array.isArray(feed.entity))) throw new Error("Invalid realtime snapshot");
  const mode = feed.header.incrementality;
  if (mode != null && mode !== 0 && mode !== "0" && mode !== "FULL_DATASET") {
    throw new Error("Differential realtime feeds are not complete snapshots");
  }
}
